import { AutocompleteProps } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import CoreAutocomplete from "../select/Autocomplete";
import CoreTextField, { CoreTextFieldProps } from "../form/TextField";

export function CoreDurationPicker({
	value,
	defaultValue,
	onChange,
	error,
	units = { hours: true, minutes: true },
	maxValue,
	minValue = 0,
	formatDuration,
	placeholder = units.days ? "DD:HH:MM" : "HH:MM",
	disabledTooltip,
	slotProps,
	fontSize,
	...autocompleteProps
}: {
	/** The default value. The number value is treated as milliseconds. */
	defaultValue?: number;
	/** The controlled value. */
	value?: number;
	/** Units to display/allow. */
	units?: { days?: boolean; hours: true; minutes: true };
	/** The maximum value. */
	maxValue?: number;
	/** The minimum value. */
	minValue?: number;
	/** Callback fired when the selected value changes. */
	onChange?: (d: number) => void;
	/** Controlled error state */
	error?: ReactNode;
	/** Formatter for duration display */
	formatDuration?: (d: number) => string;
	/** Placeholder text */
	placeholder?: string;
	/** Tooltip text when disabled */
	disabledTooltip?: ReactNode;
	/** Font size of the input field and dropdown menu */
	fontSize?: string;
	/** Subcomponent props */
	slotProps?: {
		textField?: Omit<CoreTextFieldProps, "placeholder" | "error" | "onChange" | "onBlur" | "disabledTooltip">;
	};
} & Omit<
	AutocompleteProps<number, false, true, true>,
	| "freeSolo"
	| "value"
	| "defaultValue"
	| "options"
	| "onChange"
	| "inputValue"
	| "onInputChange"
	| "getOptionLabel"
	| "renderInput"
	| "disableClearable"
>) {
	const [inputValue, setInputValue] = useState("");
	const [formatError, setFormatError] = useState(false);
	const hasTimeRangeError = (newValue: number) =>
		(maxValue != null && newValue > maxValue) || (minValue != null && newValue < minValue);
	useEffect(() => {
		try {
			const newValue = parseFreeInput(inputValue);
			setFormatError(false);
			if (!hasTimeRangeError(newValue)) {
				setValueError(false);
			}
		} catch (_) {
			setFormatError(true);
		}
	}, [inputValue]);

	const initialValue = value ?? defaultValue;
	const [valueError, setValueError] = useState(initialValue != null && hasTimeRangeError(initialValue));
	useEffect(() => {
		if (value != null) {
			setValueError(hasTimeRangeError(value));
		}
	}, [value]);

	const formatNegativeSign = (duration: number) => (duration < 0 ? "-" : "");

	const formatHoursMinutesDuration = (duration: number, padHours?: boolean) => {
		const totalMinutes = duration / (60 * 1000);
		const hours = Math.trunc(totalMinutes / 60);
		const minutes = Math.round(totalMinutes % 60);
		const hoursString = padHours ? String(Math.abs(hours)).padStart(2, "0") : Math.abs(hours);
		return `${formatNegativeSign(duration)}${hoursString}:${String(Math.abs(minutes)).padStart(2, "0")}`;
	};

	const formatDaysDuration = (duration: number) => {
		const days = Math.trunc(duration / (24 * 60 * 60 * 1000));
		const remainingDuration = duration - days * 24 * 60 * 60 * 1000;
		return `${formatNegativeSign(duration)}${Math.abs(days)}:${formatHoursMinutesDuration(Math.abs(remainingDuration), true)}`;
	};

	const getFormattedDuration = (duration?: number) => {
		if (duration == null) {
			return "";
		}
		if (formatDuration) {
			return formatDuration(duration);
		}
		if (units.days) {
			return formatDaysDuration(duration);
		} else {
			return formatHoursMinutesDuration(duration);
		}
	};

	const parseFreeInput = (input: string) => {
		const matches =
			/^(?:(?:(\d+)\s*days?)?\s*(?:(\d+)\s*hours?)?\s*(?:(\d+)\s*minutes?)?|(\d+)(?:\s*:\s*(\d{2}))?(?:\s*:\s*(\d{2}))?)$/i.exec(
				input,
			);
		if (!matches) {
			throw new Error("Invalid format");
		}
		const [, days1, hours1, minutes1, unit1, unit2, unit3] = matches;

		const unitsCount = (unit1 ? 1 : 0) + (unit2 ? 1 : 0) + (unit3 ? 1 : 0);
		const mainUnit = units.days || unitsCount === 3 ? "days" : "hours";
		const daysString = days1 ?? (mainUnit === "days" ? unit1 : undefined);
		const days = daysString ? Number(daysString) : 0;
		const hoursString = hours1 ?? (mainUnit === "hours" ? unit1 : unit2);
		const hours = hoursString ? Number(hoursString) : 0;
		const minutesString = minutes1 ?? (mainUnit === "hours" ? unit2 : unit3);
		const minutes = minutesString ? Number(minutesString) : 0;
		if (
			isNaN(days) ||
			isNaN(hours) ||
			isNaN(minutes) ||
			(hoursString && minutes > 59) ||
			(daysString && (hours > 23 || minutes > 59)) ||
			(!daysString && !hoursString && !minutesString)
		) {
			throw new Error("Invalid time");
		}
		return ((days * 24 + hours) * 60 + minutes) * 60 * 1000;
	};

	const onDurationChange = (newValue: number) => {
		if (hasTimeRangeError(newValue)) {
			setValueError(true);
		} else {
			onChange?.(newValue);
		}
		setInputValue(getFormattedDuration(newValue));
	};

	const onValueChange = (_event: unknown, newValue: number | string | (number | string)[] | null) => {
		if (typeof newValue === "string") {
			if (newValue.trim()) {
				try {
					onDurationChange(parseFreeInput(newValue.trim()));
				} catch (e) {
					console.error(e);
					const oldValue = value ?? defaultValue ?? 0;
					onDurationChange(oldValue);
				}
			}
		} else if (typeof newValue === "number") {
			onDurationChange(newValue);
		}
	};

	const maxHoursOption = Math.min(Math.floor((maxValue ?? Number.MAX_SAFE_INTEGER) / (60 * 60 * 1000)), 24);
	const minHoursOption = Math.ceil(minValue / (60 * 60 * 1000));
	const options = units.days
		? []
		: Array.from({ length: maxHoursOption - minHoursOption }, (_, i) => (i + minHoursOption) * 60 * 60 * 1000);

	return (
		<CoreAutocomplete
			{...autocompleteProps}
			freeSolo
			value={value}
			defaultValue={defaultValue ?? 0}
			options={options}
			onChange={onValueChange}
			inputValue={inputValue}
			onInputChange={(_, text) => setInputValue(text)}
			getOptionLabel={(value) => (typeof value === "string" ? value : getFormattedDuration(value))}
			renderInput={(props) => (
				<CoreTextField
					placeholder={placeholder}
					disabledTooltip={disabledTooltip}
					{...props}
					error={error || formatError || valueError}
					{...slotProps?.textField}
					onBlur={() => {
						if (!formatError) {
							onValueChange(undefined, inputValue);
						}
					}}
					sx={
						fontSize
							? {
									"& .MuiInputBase-root": {
										fontSize,
									},
								}
							: undefined
					}
				/>
			)}
			disableClearable
			fontSize={fontSize}
		/>
	);
}
